import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationMapPicker } from '../../../shared/components';
import { useFieldOperationsSettings } from '../context/FieldOperationsSettingsContext';
import { listFieldLocations } from '../services/fieldOperationsService';
import type { FieldLocation, FieldTargetKind, FieldVisitPlace } from '../types/FieldOperations';
import { hasVisitPlace } from '../visitPlace';
import styles from './VisitPlaceFields.module.css';

type VisitPlaceMode = 'none' | 'location' | 'custom';

type VisitPlaceFieldsProps = {
  targetKind: Extract<FieldTargetKind, 'calendarEvent' | 'task'>;
  value?: FieldVisitPlace;
  onChange: (next: FieldVisitPlace | undefined) => void;
  disabled?: boolean;
};

const modeOf = (place?: FieldVisitPlace): VisitPlaceMode => {
  if (place?.locationId) {
    return 'location';
  }
  return hasVisitPlace(place) ? 'custom' : 'none';
};

const toNumberOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toInputValue = (value?: number): string => (value == null ? '' : String(value));

/**
 * Optional destination shown on appointment and task editors. Renders nothing unless the tenant
 * turned field operations on and allows check-ins against this kind of job.
 */
export const VisitPlaceFields: React.FC<VisitPlaceFieldsProps> = ({ targetKind, value, onChange, disabled }) => {
  const { t } = useTranslation(['fieldOperations', 'common']);
  const { allowsTarget, settings } = useFieldOperationsSettings();
  const enabled = allowsTarget(targetKind);
  const [locations, setLocations] = useState<FieldLocation[]>([]);
  const [mode, setMode] = useState<VisitPlaceMode>(() => modeOf(value));
  const lastEmitted = useRef(value);

  // Follow the value only when the parent swapped it out (loading another job, resetting the
  // form). Echoes of our own edits are ignored so clearing every custom field does not collapse
  // the inputs the user is still typing into.
  useEffect(() => {
    if (value === lastEmitted.current) {
      return;
    }

    lastEmitted.current = value;
    setMode(modeOf(value));
  }, [value]);

  const emit = (next: FieldVisitPlace | undefined) => {
    lastEmitted.current = next;
    onChange(next);
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    void listFieldLocations()
      .then((rows) => {
        if (!cancelled) {
          setLocations(rows);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const locationOptions = useMemo(
    () => locations.filter((location) => location.isActive || location.id === value?.locationId),
    [locations, value?.locationId],
  );

  if (!enabled) {
    return null;
  }

  const patch = (changes: Partial<FieldVisitPlace>) => {
    const next: FieldVisitPlace = { ...value, ...changes };
    emit(hasVisitPlace(next) ? next : undefined);
  };

  const applyMapPin = (latitude: number, longitude: number) => {
    patch({
      latitude,
      longitude,
      radiusMeters: value?.radiusMeters ?? settings.defaultRadiusMeters,
    });
  };

  const changeMode = (next: VisitPlaceMode) => {
    setMode(next);
    if (next === 'none') {
      emit(undefined);
      return;
    }

    if (next === 'location') {
      emit(value?.locationId ? { locationId: value.locationId } : undefined);
      return;
    }

    const rest: FieldVisitPlace = { ...value, locationId: undefined };
    emit(hasVisitPlace(rest) ? rest : undefined);
  };

  return (
    <div className={styles.wrapper}>
      <span className={styles.title}>{t('visitPlace')}</span>
      <p className={styles.hint}>{t('visitPlaceHint')}</p>

      <select
        className="text-input"
        value={mode}
        disabled={disabled}
        onChange={(event) => changeMode(event.target.value as VisitPlaceMode)}
        aria-label={t('visitPlace')}
      >
        <option value="none">{t('visitPlaceNone')}</option>
        <option value="location">{t('visitPlaceSavedSite')}</option>
        <option value="custom">{t('visitPlaceCustom')}</option>
      </select>

      {mode === 'location' ? (
        <select
          className="text-input"
          value={value?.locationId ?? ''}
          disabled={disabled}
          onChange={(event) => emit(event.target.value ? { locationId: event.target.value } : undefined)}
          aria-label={t('visitPlaceSavedSite')}
        >
          <option value="">{t('visitPlaceNone')}</option>
          {locationOptions.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      ) : null}

      {mode === 'custom' ? (
        <>
          <input
            className="text-input"
            value={value?.address ?? ''}
            disabled={disabled}
            placeholder={t('visitPlaceAddress')}
            aria-label={t('visitPlaceAddress')}
            onChange={(event) => patch({ address: event.target.value || undefined })}
          />
          <div className={styles.row}>
            <input
              className={`text-input ${styles.narrow}`}
              type="number"
              step="any"
              value={toInputValue(value?.latitude)}
              disabled={disabled}
              placeholder={t('visitPlaceLatitude')}
              aria-label={t('visitPlaceLatitude')}
              onChange={(event) => patch({ latitude: toNumberOrUndefined(event.target.value) })}
            />
            <input
              className={`text-input ${styles.narrow}`}
              type="number"
              step="any"
              value={toInputValue(value?.longitude)}
              disabled={disabled}
              placeholder={t('visitPlaceLongitude')}
              aria-label={t('visitPlaceLongitude')}
              onChange={(event) => patch({ longitude: toNumberOrUndefined(event.target.value) })}
            />
            <input
              className={`text-input ${styles.narrow}`}
              type="number"
              min={10}
              max={10000}
              value={toInputValue(value?.radiusMeters)}
              disabled={disabled}
              placeholder={t('visitPlaceRadius')}
              aria-label={t('visitPlaceRadius')}
              onChange={(event) => patch({ radiusMeters: toNumberOrUndefined(event.target.value) })}
            />
          </div>
          <LocationMapPicker
            compact
            latitude={value?.latitude}
            longitude={value?.longitude}
            radiusMeters={value?.radiusMeters}
            disabled={disabled}
            onChange={applyMapPin}
          />
        </>
      ) : null}
    </div>
  );
};
